import React from 'react';
import {
  Plugins,
  Atom,
  StyleAttributeImm,
  AtomRenderProps,
} from '@packages/systems/wfdl';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';

import {TTypeConstructor, TTypeApplication} from '@packages/systems/wfdl-ast';
import {extraAtomFields} from '@packages/shared/constants/Atoms';
import {COLLECTION_LIST_QUERY_MODES} from '@packages/systems/dynamo/constants';
import {isDynamoWrapper} from '@packages/utilities/elementTypeUtils';
import {
  getDynListQueryModeFromElement,
  isItemRefSetBoundList,
} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {getChildren} from '@packages/utilities/data-utils';

const maybeAddAriaLabel = (
  ariaLabel: string | StyleAttributeImm | Map<string, string>
) => {
  return ariaLabel ? ariaLabel : 'List';
};

const Pagination = Atom.createRender(
  ({
    attributes,
    classes,
    data,
    generateAttributes,
    innerHTML,
    renderChild,
  }: AtomRenderProps) => {
    // @ts-expect-error - TS18046 - 'data' is of type 'unknown'.
    const Tag = data.tag ?? 'div';
    const newAttributes = attributes
      .set('role', 'navigation')
      // @ts-expect-error - No overload matches this call
      .update('aria-label', maybeAddAriaLabel);

    return (
      <Tag {...generateAttributes(newAttributes, classes)}>
        {innerHTML || getChildren(data).map((child) => renderChild(child))}
      </Tag>
    );
  }
);

const config = Atom.create({
  displayName: 'Pagination',
  className: 'w-pagination-wrapper',
  style: {
    base: {
      main: {
        noPseudo: {
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
        },
      },
    },
  },
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Pagination'), iconName('Pagination')),
  },
  showEmpty: false,
  render: Pagination,
  constraints: {
    parent: [
      {
        is: {
          check: (element) =>
            isDynamoWrapper(element) && !isItemRefSetBoundList(element),
          name: 'Collection List Wrapper that is linked to a Collection',
        },
        rule: 'ExactlyOne',
      },
      {
        is: {
          check: (element) =>
            isDynamoWrapper(element) &&
            getDynListQueryModeFromElement(element) !==
              COLLECTION_LIST_QUERY_MODES.CURATED,
          name: 'Collection List Wrapper in dynamic mode',
        },
        rule: 'ExactlyOne',
      },
    ],
    ancestors: [
      {is: 'DynamoItem', rule: 'Forbid'},
      {is: 'DynamoList', rule: 'Forbid'},
      {is: 'DynamoEmpty', rule: 'Forbid'},
      {is: 'Pagination', rule: 'Forbid'},
    ],
    descendants: [
      {is: 'PaginationPrevious', rule: 'AtLeastOne'},
      {is: 'PaginationNext', rule: 'AtLeastOne'},
      {is: 'DynamoWrapper', rule: 'Forbid'},
    ],
  },
  dataType: TTypeApplication({
    con: TTypeConstructor(
      qualify(Plugins.name('Pagination'), typeName('Pagination'))
    ),
    arg: extraAtomFields,
  }),
  showSettingsInDescendants: true,
});

export default config;
